import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InlineInputField, InlineSelect } from "./fields";
import { Form } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";
import { useProgramContext } from "./ProgramContext";
import { useAppSelector } from "../../../redux/hooks";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "../../../utils/marketplaceConstants";
import { useFetch } from "../../../hooks/api";
import { useQuery } from "@tanstack/react-query";
import { useGetV4AdvReportByAd } from "../../../modules/perf_reports/api/ads/v4AdvReportByAd";
import { useTagsQueryOptions } from "../../../modules/advertising/api/useTags";
import { TAGGER_TO_TAG_ENTITY_TYPE } from "./taggingEntityTypes";
import { css } from "../../../../styled-system/css";
import { FIXED_AMOUNT_ACTIONS } from "./constants";

const PriceForm = ({ action, fieldName, control, isReadOnly = false }) => {
  const { t } = useTranslation(["programs"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const days = useWatch({ name: `${fieldName}.values.1`, control });

  const currency = MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace];
  const adjustOptions = [
    { value: "adjust_fixed", label: currency },
    { value: "adjust_percent", label: "%" },
  ];
  const isFixedAmount = FIXED_AMOUNT_ACTIONS.includes(action);

  if (action === "SET_BID_CPC") {
    let daysCount = 2;
    try {
      daysCount = days && parseInt(days) > 0 ? parseInt(days) : 2;

      // oxlint-disable-next-line no-unused-vars
    } catch (e) {
      daysCount = 2;
    }
    return (
      <>
        <span className="px-3">{t(($) => $.programs.harvestKeyword["of last"])}</span>
        <InlineInputField
          fieldName={`${fieldName}.values.1`}
          control={control}
          rules={{ required: true }}
          isDisabled={isReadOnly}
        />
        <span className="px-3">
          {" "}
          {t(($) => $.programs.harvestKeyword.days, { count: daysCount })}
          {t(($) => $.programs.harvestKeyword["adjusted by"])}
        </span>
        <InlineSelect
          options={adjustOptions}
          fieldName={`${fieldName}.operator`}
          control={control}
          rules={{ required: true }}
          disabled={isReadOnly}
        />
        <InlineInputField
          fieldName={`${fieldName}.values.0`}
          control={control}
          rules={{ required: true }}
          isDisabled={isReadOnly}
        />
      </>
    );
  }

  return (
    <>
      {action !== "SET_PRICE" && !isFixedAmount && (
        <InlineSelect
          options={adjustOptions}
          fieldName={`${fieldName}.operator`}
          control={control}
          rules={{ required: true }}
          disabled={isReadOnly}
        />
      )}
      <InlineInputField
        fieldName={`${fieldName}.values.0`}
        control={control}
        rules={{ required: true }}
        isDisabled={isReadOnly}
        symbol={isFixedAmount ? currency : null}
      />
    </>
  );
};

const HarvestKeywordAction = ({ fieldName, control }) => {
  const { t } = useTranslation(["programs"]);
  const { resetField } = useFormContext();
  const targetsSelection = useWatch({ name: "targets_selection", control });
  const targetAdIds = useMemo(() => targetsSelection?.ad_ids ?? [], [targetsSelection?.ad_ids]);

  const selectedAdId = useWatch({ name: `${fieldName}.values.0`, control });
  const selectedAdIdSet = useMemo(() => new Set(targetAdIds), [targetAdIds]);
  const selectedAdIdsForQuery = useMemo(
    () => Array.from(new Set([...targetAdIds, selectedAdId].filter(Boolean))),
    [targetAdIds, selectedAdId],
  );

  const adFilters = useMemo(() => {
    if (!selectedAdIdsForQuery.length) {
      return [];
    }
    return [{ id: "ad_id", value: { op: "anyof", value: selectedAdIdsForQuery } }];
  }, [selectedAdIdsForQuery]);

  const byAdOptionsGetter = useGetV4AdvReportByAd();
  const adPlansQuery = useQuery({
    ...byAdOptionsGetter({
      pagination: {
        pageIndex: 0,
        pageSize: Math.max(selectedAdIdsForQuery.length, 10),
      },
      sorting: [],
      filters: adFilters,
      search: "",
    }),
    enabled: selectedAdIdsForQuery.length > 0,
  });
  const ads = adPlansQuery.data?.data ?? [];

  const { data: adGroupData } = useFetch(
    ["adGroups", selectedAdId],
    "/api/ad_groups",
    {
      advertisement: selectedAdId,
    },
    {
      enabled: !!selectedAdId && Number.isInteger(Number(selectedAdId)),
      select: (res) => res.data ?? [],
    },
  );

  const adGroupOptions =
    adGroupData?.map((adGroup) => {
      return { value: adGroup.ad_group_id, label: adGroup.ad_group_name };
    }) ?? [];

  const options =
    ads?.filter((ad) => selectedAdIdSet.has(ad.ad_id)).map((ad) => ({ value: ad.ad_id, label: ad.ad_name })) ?? [];
  const selectedAd = ads?.find((ad) => ad.ad_id === selectedAdId);
  return (
    <>
      <InlineSelect
        title={t(($) => $.programs.harvestKeyword["select media plan"])}
        fieldName={`${fieldName}.values.0`}
        control={control}
        rules={{ required: true }}
        options={options}
        onChange={(adId) => {
          // An ad group belongs to its media plan. resetField also replaces the default, so the picker below,
          // remounted for the new plan, doesn't fall back to the old plan's saved ad group.
          if (adId !== selectedAdId) resetField(`${fieldName}.values`, { defaultValue: [adId] });
        }}
      />
      {!selectedAd && (
        <span className="text-danger">{t(($) => $.programs.harvestKeyword["Please select a media plan"])}</span>
      )}
      {selectedAd?.ad_source === "Imported" && adGroupOptions.length > 0 && (
        <>
          {t(($) => $.programs.harvestKeyword["ad group"])}
          <InlineSelect
            key={selectedAdId}
            title={t(($) => $.programs.harvestKeyword["select ad group"])}
            fieldName={`${fieldName}.values.1`}
            control={control}
            rules={{ required: true }}
            options={adGroupOptions}
          />
        </>
      )}
    </>
  );
};

const TagActionField = ({ fieldName, control, isReadOnly }) => {
  const { t } = useTranslation(["programs"]);
  const entityType = useWatch({ name: "entity_type", control }) ?? "advertisement";
  const tagQueryOptions = useTagsQueryOptions({ entity_type: TAGGER_TO_TAG_ENTITY_TYPE[entityType] });
  const { data: tags, isLoading } = useQuery(tagQueryOptions);

  const formattedTagResults = useMemo(() => {
    if (!tags?.results) {
      return [];
    }
    return tags.results.map((tag) => ({
      value: tag.id,
      label: tag.name,
    }));
  }, [tags?.results]);

  if (isLoading) {
    return (
      <span className={css({ marginX: "medium", color: "gray.500" })}>
        {t(($) => $.programs.tag["Loading tags..."])}
      </span>
    );
  }

  return (
    <InlineSelect
      fieldName={`${fieldName}.values.0`}
      control={control}
      rules={{ required: true }}
      options={formattedTagResults}
      disabled={isReadOnly}
      title={t(($) => $.programs.tag["Select a tag"])}
    />
  );
};

// Actions in one group take the same values with the same meaning, so switching between them keeps what was
// entered. SET_* amounts are absolute, unlike the relative INCREASE_/LOWER_ ones, so switching to or from them resets.
const ACTIONS_SHARING_VALUES = [
  ["INCREASE_PRICE", "LOWER_PRICE"],
  ["INCREASE_BID", "LOWER_BID"],
  ["INCREASE_BUDGET", "LOWER_BUDGET"],
  ["INCREASE_BASE_BID", "LOWER_BASE_BID"],
  ["INCREASE_BID_MULTIPLIER", "LOWER_BID_MULTIPLIER"],
  ["HARVEST_QUERY", "HARVEST_QUERY_PHRASE", "HARVEST_ASIN"],
  ["NEGATE_QUERY_EXACT", "NEGATE_QUERY_PHRASE", "NEGATE_ASIN"],
];

const valuesGroup = (action) => ACTIONS_SHARING_VALUES.find((group) => group.includes(action))?.[0] ?? action;

const ActionForm = ({ fieldName, control, initialAction, isReadOnly = false }) => {
  const { t } = useTranslation(["programs"]);
  const { engine } = useProgramContext();
  const { resetField, setValue } = useFormContext();
  const actionOptions = {
    pricing: [
      { value: "SET_PRICE", label: t(($) => $.programs.action["Set price"]) },
      { value: "INCREASE_PRICE", label: t(($) => $.programs.action["Increase price"]) },
      { value: "LOWER_PRICE", label: t(($) => $.programs.action["Decrease price"]) },
    ],
    bidding:
      initialAction ?
        [
          { value: "SET_BID", label: t(($) => $.programs.action["Set bid"]) },
          { value: "USE_ALGO", label: t(($) => $.programs.action["Use optimal bid"]) },
        ]
      : [
          { value: "SET_BID", label: t(($) => $.programs.action["Set bid"]) },
          { value: "INCREASE_BID", label: t(($) => $.programs.action["Increase bid"]) },
          { value: "LOWER_BID", label: t(($) => $.programs.action["Decrease bid"]) },
          { value: "USE_ALGO", label: t(($) => $.programs.action["Use optimal bid"]) },
          { value: "SET_BID_CPC", label: t(($) => $.programs.action["Set bid based on average CPC"]) },
          { value: "DO_NOTHING", label: t(($) => $.programs.action["Keep current bid"]) },
          // { value: "PAUSE", label: "Pause keyword or target" },
        ],
    budget: [
      { value: "SET_BUDGET", label: t(($) => $.programs.action["Set budget"]) },
      { value: "INCREASE_BUDGET", label: t(($) => $.programs.action["Increase budget"]) },
      { value: "LOWER_BUDGET", label: t(($) => $.programs.action["Decrease budget"]) },
      /// { value: "DISABLE_BIDDING", label: "Pause bidding" },
      // { value: "ENABLE_BIDDING", label: "Enable bidding" },
      { value: "INCREASE_BID_MULTIPLIER", label: t(($) => $.programs.action["Increase bid multiplier"]) },
      { value: "LOWER_BID_MULTIPLIER", label: t(($) => $.programs.action["Decrease bid multiplier"]) },
      { value: "SET_BID_MULTIPLIER", label: t(($) => $.programs.action["Set bid multiplier"]) },
      { value: "DO_NOTHING", label: t(($) => $.programs.action["Do nothing"]) },
    ],
    harvesting: [
      { value: "HARVEST_QUERY", label: t(($) => $.programs.action["Harvest Query Exact"]) },
      { value: "HARVEST_QUERY_PHRASE", label: t(($) => $.programs.action["Harvest Query Phrase"]) },
      { value: "HARVEST_ASIN", label: t(($) => $.programs.action["Harvest ASIN"]) },
      { value: "NEGATE_QUERY_EXACT", label: t(($) => $.programs.action["Negate Query Exact"]) },
      { value: "NEGATE_QUERY_PHRASE", label: t(($) => $.programs.action["Negate Query Phrase"]) },
      { value: "NEGATE_ASIN", label: t(($) => $.programs.action["Negate ASIN"]) },
    ],
    lineitem_bidding:
      initialAction ?
        [{ value: "SET_BASE_BID", label: t(($) => $.programs.action["Set base bid"]) }]
      : [
          { value: "SET_BASE_BID", label: t(($) => $.programs.action["Set base bid"]) },
          { value: "INCREASE_BASE_BID", label: t(($) => $.programs.action["Increase base bid"]) },
          { value: "LOWER_BASE_BID", label: t(($) => $.programs.action["Decrease base bid"]) },
          { value: "DO_NOTHING", label: t(($) => $.programs.action["Keep current base bid"]) },
        ],
    tagging: [{ value: "ADD_TAG", label: t(($) => $.programs.action["Add tag"]) }],
  };
  const action =
    useWatch({
      control,
      name: `${fieldName}.function`,
    }) ?? "";

  return (
    <Form.Group>
      <div className="d-flex align-items-center flex-wrap fs-standard">
        <InlineSelect
          options={actionOptions[engine]}
          fieldName={`${fieldName}.function`}
          control={control}
          rules={{ required: true }}
          disabled={isReadOnly}
          onChange={(newAction) => {
            // Also clears the default, which an emptied input would otherwise fall back to.
            if (valuesGroup(newAction) !== valuesGroup(action)) {
              resetField(`${fieldName}.values`, { defaultValue: [] });
            }
            // Set* amounts are absolute and multipliers always apply as given, so their operator is fixed. Keeps the
            // saved operator truthful: a leftover percent would read as one in the rule summary.
            if (FIXED_AMOUNT_ACTIONS.includes(newAction) || newAction.includes("BID_MULTIPLIER")) {
              setValue(`${fieldName}.operator`, "adjust_fixed");
            }
          }}
        />
        {/* Keyed by group so clearing the values remounts their inputs: a mounted input whose value is removed
            shows the value it mounted with, which the form no longer holds. */}
        <Fragment key={valuesGroup(action)}>
          {action.includes("BID_MULTIPLIER") && (
            <>
              <InlineSelect
                fieldName={`${fieldName}.values.1`}
                control={control}
                rules={{ required: true }}
                defaultValue="PLACEMENT_TOP"
                options={[
                  { value: "PLACEMENT_TOP", label: t(($) => $.programs.placement["Top of Search"]) },
                  { value: "PLACEMENT_PRODUCT_PAGE", label: t(($) => $.programs.placement["Detail Page"]) },
                  { value: "PLACEMENT_REST_OF_SEARCH", label: t(($) => $.programs.placement["Rest of Search"]) },
                ]}
                disabled={isReadOnly}
              />
              <InlineInputField
                fieldName={`${fieldName}.values.0`}
                control={control}
                rules={{ required: true }}
                isDisabled={isReadOnly}
              />{" "}
              <span>%</span>
            </>
          )}
          {(action.includes("HARVEST_QUERY") || action === "HARVEST_ASIN") && (
            <>
              {t(($) => $.programs.harvestKeyword["in to"])}{" "}
              <HarvestKeywordAction fieldName={fieldName} control={control} />
            </>
          )}
          {action === "ADD_TAG" && <TagActionField fieldName={fieldName} control={control} isReadOnly={isReadOnly} />}
          {(action.includes("NEGATE_QUERY") || action === "NEGATE_ASIN") && (
            <>
              {t(($) => $.programs.harvestKeyword.in)}{" "}
              <InlineSelect
                fieldName={`${fieldName}.values.0`}
                control={control}
                rules={{ required: true }}
                options={[
                  { value: "source", label: t(($) => $.programs.negateScope["Source Campaign"]) },
                  { value: "all", label: t(($) => $.programs.negateScope["All Source Campaigns"]) },
                ]}
                disabled={isReadOnly}
              />
            </>
          )}
          {![
            "RESET_ACC",
            "NEGATE",
            "PAUSE",
            "REVERT_TO_ALGO",
            "DO_NOTHING",
            "x",
            "USE_ALGO",
            "INCREASE_BID_MULTIPLIER",
            "LOWER_BID_MULTIPLIER",
            "SET_BID_MULTIPLIER",
            "HARVEST_QUERY",
            "HARVEST_QUERY_PHRASE",
            "HARVEST_ASIN",
            "NEGATE_QUERY_EXACT",
            "NEGATE_QUERY_PHRASE",
            "NEGATE_ASIN",
            "ADD_TAG",
          ].includes(action) && (
            <PriceForm
              action={action}
              fieldName={`${fieldName}`}
              control={control}
              rules={{ required: true }}
              actionOptions={actionOptions[engine]}
              isReadOnly={isReadOnly}
            />
          )}
        </Fragment>
      </div>
    </Form.Group>
  );
};

export default ActionForm;
